import { createContext, FC, useContext, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import { v4 as uuidv4 } from 'uuid'
import { QAErrorMessages } from 'components/QA/Assessments/constants'
import { useQAComments } from 'components/QA/Assessments/hooks/useQAComments'
import { sortCommentsByItemsMap } from 'components/QA/Assessments/helpers'
import { parseReferenceString } from 'fhir/utils'
import { Communication } from 'generated/graphql'
import { useQAAssessmentContext } from './QAAssessmentContext'

export type CommentsByTask = { [taskId: string]: Communication[] }

interface QAAssessmentCommentsContextType {
  commentsByTask: CommentsByTask
  sortedComments: Communication[]
  focusedCommentId: string | undefined
  setFocusedCommentId: React.Dispatch<React.SetStateAction<string | undefined>>
  commentsLoading: boolean
  reloadComments: () => Promise<void>
  addNewComment: (questionLinkId: string) => Communication
  removeNewComment: (commentId: string) => void
  newComments: Communication[]
  existingComments: Communication[]
}

const QAAssessmentCommentsContext = createContext<QAAssessmentCommentsContextType>({
  commentsByTask: {},
  sortedComments: [],
  focusedCommentId: undefined,
  setFocusedCommentId: () => {},
  reloadComments: async () => {},
  commentsLoading: false,
  addNewComment: () => ({} as Communication),
  removeNewComment: () => {},
  newComments: [],
  existingComments: []
})

export const useQAAssessmentCommentsContext = (): QAAssessmentCommentsContextType => {
  return useContext(QAAssessmentCommentsContext)
}

interface QAAssessmentCommentsProviderProps {
  children: React.ReactNode
}

export const QAAssessmentCommentsProvider: FC<QAAssessmentCommentsProviderProps> = ({
  children
}) => {
  const [newComments, setNewComments] = useState<Communication[]>([])
  const { qaTaskList, qaAssessmentItems, qaNurse, caseManager, qaTask, qaAssessmentId } =
    useQAAssessmentContext()

  const qaTaskListIds = useMemo(
    () => qaTaskList.map((task) => task.id).filter((t): t is string => !!t),
    [qaTaskList]
  )
  const { comments, commentsError, commentsLoading, fetchComments } = useQAComments({
    taskIds: qaTaskListIds
  })

  const [focusedCommentId, setFocusedCommentId] = useState<string>()

  const addNewComment = (questionLinkId: string): Communication => {
    const calculatedQaNurseRef = qaNurse?.id ? `Practitioner/${qaNurse?.id}` : undefined
    const qaNurseRef = qaNurse?.reference ?? calculatedQaNurseRef
    const [_, derivedNurseId] = qaNurseRef ? parseReferenceString(qaNurseRef) : []
    const qaNurseId = qaNurse?.id ?? derivedNurseId
    const questionnaireRef = `QuestionnaireResponse/${qaAssessmentId}`
    const caseManagerRef = `Practitioner/${caseManager?.id}`
    const newComment: Communication = {
      id: uuidv4().toString(),
      resourceType: 'Communication',
      basedOn: [{ reference: `Task/${qaTask?.id}`, id: qaTask?.id ?? '' }],
      partOf: [{ reference: questionnaireRef, id: qaAssessmentId }],
      about: [{ reference: questionnaireRef, id: qaAssessmentId }],
      status: 'in-progress',
      topic: { id: questionLinkId },
      recipient: [
        {
          reference: caseManagerRef,
          id: caseManager?.id ?? '',
          display: caseManager?.display ?? ''
        }
      ],
      sender: { reference: qaNurseRef, id: qaNurseId, display: qaNurse?.display ?? '' },
      sent: dayjs().toISOString(),
      reasonCode: [{ text: 'qa' }]
    }
    setNewComments((prevComments) => [...prevComments, newComment])
    return newComment
  }
  const removeNewComment = (commentId: string): void => {
    setNewComments((prevComments) => prevComments.filter((c) => c.id !== commentId))
  }

  // Comments are grouped by task id
  const commentsByTask = useMemo(() => {
    const commentsByTask: CommentsByTask = {}
    const allComents = [...comments, ...newComments]

    allComents.forEach((comment) => {
      const taskIds =
        comment.basedOn?.map((basedOn) => basedOn.id)?.filter((i): i is string => !!i) ?? []
      taskIds.forEach((taskId) => {
        commentsByTask[taskId] = commentsByTask[taskId] ?? []
        commentsByTask[taskId].push(comment)
      })
    })

    return commentsByTask
  }, [comments, newComments])

  if (commentsError) {
    throw new Error(QAErrorMessages.CommentsError)
  }

  const sortedComments = useMemo((): Communication[] => {
    if (commentsByTask && Object.values(commentsByTask).length > 0 && qaAssessmentItems) {
      const allCommentsArray = Object.values(commentsByTask).reduce(
        (accumulator, currentValue) => accumulator.concat(currentValue),
        [] as Communication[]
      )

      return sortCommentsByItemsMap(allCommentsArray, qaAssessmentItems)
    }

    return []
  }, [qaAssessmentItems, commentsByTask])

  const reloadComments = async (): Promise<void> => {
    await fetchComments()
  }

  return (
    <QAAssessmentCommentsContext.Provider
      value={{
        commentsByTask,
        sortedComments,
        focusedCommentId,
        setFocusedCommentId,
        reloadComments,
        commentsLoading,
        addNewComment,
        removeNewComment,
        newComments,
        existingComments: comments
      }}
    >
      {children}
    </QAAssessmentCommentsContext.Provider>
  )
}

export default QAAssessmentCommentsContext
